// Used in login-routing.module.ts

// Angular
import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";

// RXJS/Store
import * as RouterStore from "#core/router-store";
import { Store, select } from "@ngrx/store";
import { Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import * as LoginStore from "../../store";

// Services/Types/Helpers
import { CookieService } from "#core/services/cookie.service";
import { LocalStorage } from "#core/services/local-storage.service";
import { SampleCenterRequest } from "@carnegie-learning/data-access-angular-passport";

@Component({
    selector: "app-login-sample-center",
    templateUrl: "./login-sample-center.component.html",
    styleUrls: ["./login-sample-center.component.scss"],
    standalone: false
})
export class LoginSampleCenterComponent implements OnInit, OnDestroy {
    intervalDelay = 100;
    maintenanceMode: boolean = (<any>window).maintenanceMode;
    bannerStatus$?: Observable<boolean>;
    bannerMessage$?: Observable<string>;
    bannerColor$?: Observable<string>;
    showProgressSpinner = true;
    private ngUnsubscribe = new Subject();
    cookieChecker: string;

    constructor(
        private router: Router,
        public loginStore: Store<LoginStore.UserState>,
        public routerStore: Store<RouterStore.State>,
        private localStorage: LocalStorage,
        private cookieService: CookieService
    ) {}

    ngOnInit() {
        this.bannerStatus$ = this.loginStore.pipe(select(LoginStore.getLoginBannerStatus));
        this.bannerMessage$ = this.loginStore.pipe(select(LoginStore.getLoginBannerMessage));
        this.bannerColor$ = this.loginStore.pipe(select(LoginStore.getLoginBannerColor));
        this.loginStore
            .pipe(select(LoginStore.getLoginBannerColor))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((data) => {
                if (data !== "primary") {
                    this.showProgressSpinner = false;
                }
            });

        // Need to subscribe whether oauth login was a success then retrieve profile
        this.loginStore
            .pipe(select(LoginStore.getSampleCenterOAuthSuccess))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((state) => {
                if (state) {
                    // will be true if success
                    this.cookieChecker = (<any>window).setInterval(() => {
                        const CLTOKEN = this.cookieService.get("CL_TOKEN2");
                        if (CLTOKEN) {
                            this.retrieveProfile();
                        }
                    }, this.intervalDelay);
                }
            });

        if (this.maintenanceMode) {
            this.loginStore.dispatch(LoginStore.userLoginMaintenance());
            this.showProgressSpinner = false;
        } else {
            this.routerStore
                .pipe(select(RouterStore.getRouterState))
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe((data) => {
                    // PAS-13304 due to reload from usher-payload.helper need to check if page was reloaded
                    const demoReload: boolean = this.localStorage.get('demo-reload');
                    if (
                        data &&
                        data.state.url.includes("cl-sample-center/authorize") &&
                        !demoReload
                    ) {
                        const oAuthToken: string = data.state.queryParams.code,
                              logoutRedirectURL: string = data.state.queryParams.logout_redirect_url,
                              requestedProgramID: number = Number.parseInt(data.state.queryParams.program_id, 10),
                              payload: SampleCenterRequest = {clSampleCenterToken: oAuthToken};
                        this.localStorage.clear();// Clear out local storage and reset state
                        this.loginStore.dispatch(LoginStore.userProfileResetState());
                        this.loginStore.dispatch(LoginStore.registerResetState());
                        if (oAuthToken === undefined || oAuthToken === null) {
                            this.loginStore.dispatch(LoginStore.userSampleCenterLoginNoToken());
                        } else {
                            // PAS-4514 Sample Center needs to specify a redirect
                            // on logout to finish the lifecycle of a user.
                            if (logoutRedirectURL !== undefined && logoutRedirectURL !== null && logoutRedirectURL !== "null" && logoutRedirectURL !== "") {
                                this.localStorage.set("logoutRedirectURL", logoutRedirectURL);
                                this.localStorage.set("requestProgramID", requestedProgramID);
                            }
                            this.loginStore.dispatch(LoginStore.userSampleCenterLoginAttempt({payload: payload}));
                        }
                    } else if (
                        data &&
                        data.state.url.includes("cl-sample-center/authorize")
                    ) {
                        (<any>window).location.href = (<any>window).passportHost + "/#/teacher/classes";
                    }
                });
        }
    }

    retrieveProfile() {
        if (!!this.cookieChecker) {
            (<any>window).clearInterval(this.cookieChecker);
        }
        this.loginStore.dispatch(LoginStore.userProfileAttempt());
    }

    ngOnDestroy() {
        if (!!this.cookieChecker) {
            (<any>window).clearInterval(this.cookieChecker);
        }
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }

    cancel() {
        this.router.navigate(["login/form"]);
    }
}
